import { Component, ChangeDetectionStrategy, Input, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { TranslateModule } from '@ngx-translate/core';
import { BoschUiChipComponent } from '@intrack/bosch-ui-kit';
import { TransportMode } from '../../../../common/constants';
import { MapPoint, MapStop, MapTrackingPoints } from '../../../../common/models';
import { MapPolyline, StopMarkerOptions } from '../../models/tracking-map.model';
import { isNil } from 'lodash';
import { MapConfig } from '../../models/tracking-map-base.model';
import { BaseTrackingMapComponent } from '../../base/base-tracking-map.component';
import { TrackingMapsService } from '../../services/tracking-maps.service';
import { IntrackFeatureModule } from '../../../../intrack-feature.module';
import { DateWithProfileAndLocalTimezoneComponent } from '../../../profile-and-local-date/date-with-profile-and-local-timezone.component';

@Component({
  selector: 'lib-map',
  imports: [
    CommonModule,
    GoogleMapsModule,
    TranslateModule,
    IntrackFeatureModule,
    BoschUiChipComponent,
    DateWithProfileAndLocalTimezoneComponent,
  ],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss', '../../base/base-tracking-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent extends BaseTrackingMapComponent<MapStop, MapConfig> implements AfterViewInit {
  @Input() trackingTransportMode: TransportMode;
  @Input() set mapData(data: MapTrackingPoints<MapStop, MapPoint>) {
    if (data) {
      this.mapTrackingPoints = this.filterEmptyCoordinates(data);
      this.mergedPoints = this.trackingMapsService.getMergedPoints(data.points);

      this.mapConfig = {
        vehicleMarkerOptions: this.trackingMapsService.getVehicleMarkerOptions(
          this.mergedPoints[this.mergedPoints.length - 1],
          this.trackingTransportMode,
        ),
        stopMarkerOptions: this.getStopMarkerOptions(),
        stopPolylineOptions: this.getStopPolylineOptions(),
        pointMarkerOptions: this.trackingMapsService.getPointsMarkerOptions(this.mergedPoints),
        pointPolylineOptions: this.trackingMapsService.getPointsPolylineOptions(this.mapTrackingPoints.points),
      };
    }
  }

  constructor(private trackingMapsService: TrackingMapsService) {
    super();
  }

  ngAfterViewInit(): void {
    this.setMapBounds();
  }

  private setMapBounds(): void {
    this.mapRef.fitBounds(this.trackingMapsService.getBounds([...this.mapTrackingPoints.stops, ...this.mergedPoints]));
  }

  private getStopMarkerOptions(): StopMarkerOptions[] {
    return this.mapTrackingPoints.stops.map((stop: MapStop) => new StopMarkerOptions(stop, this.trackingTransportMode));
  }

  private getStopPolylineOptions(): MapPolyline[] {
    return this.mapTrackingPoints.stops.map(
      (stop: MapStop, i: number, array: MapStop[]) => new MapPolyline(stop, array[i + 1]),
    );
  }

  private filterEmptyCoordinates(data: MapTrackingPoints<MapStop, MapPoint>): MapTrackingPoints<MapStop, MapPoint> {
    return Object.entries(data).reduce<MapTrackingPoints<MapStop, MapPoint>>(
      (acc: MapTrackingPoints<MapStop, MapPoint>, [key, value]: [string, Array<MapStop | MapPoint>]) => ({
        ...acc,
        [key]: value.filter((item: MapStop | MapPoint) => !isNil(item.longitude) && !isNil(item.latitude)),
      }),
      { stops: [], points: [] },
    );
  }
}
